/** A usable number: finite. Missing, NaN and ±Infinity are all unknown. */
export function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Turns −0 into 0, so a short at its entry price never shows as "−0.00". Every other value is unchanged. */
export function withoutNegativeZero(value: number): number {
  return value === 0 ? 0 : value;
}

/**
 * Rounds to a number of decimals the way the API's C# decimal.Round does by default: halves go to the even neighbour
 * (0.0025 → 0.002, 0.0035 → 0.004). The scaled value is compared with a tolerance, so binary floating-point noise
 * (1.0000000000000002) never decides the direction.
 */
export function roundHalfEven(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  const scaled = value * factor;
  const floor = Math.floor(scaled);
  const fraction = scaled - floor;
  const epsilon = 1e-9 * Math.max(1, Math.abs(scaled));

  let rounded: number;
  if (Math.abs(fraction - 0.5) <= epsilon) rounded = floor % 2 === 0 ? floor : floor + 1;
  else rounded = fraction < 0.5 ? floor : floor + 1;

  return withoutNegativeZero(rounded / factor);
}

/**
 * A usable price or quantity: finite and above zero. Binance never prices a listed symbol at 0, so a 0 here means
 * "missing" (the old app showed it as a real price).
 */
export function isPositiveNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value > 0;
}
